import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import ContactModal from '../components/ContactModal'
import { productsApi } from '../services/api'

// Preview environment name (null on production)
const previewEnv = (() => {
  if (typeof window === 'undefined') return null
  if (window.location.hostname !== 'preview.upcast.social') return null
  const match = window.location.pathname.match(/^\/([^/]+)/)
  return match ? match[1] : null
})()

// SMM Logo
export const SMMLogo = ({ className = "w-10 h-10" }) => (
  <span className="relative inline-block">
    <img src="logo.png" alt="Upcast.social" className={className} />
    {previewEnv && (
      <span className="absolute -bottom-1 -right-1 bg-amber-500 text-[6px] font-bold text-black px-1 rounded leading-tight whitespace-nowrap">
        {previewEnv}
      </span>
    )}
  </span>
)

// Stylised brand name: "Upcast" bold + ".social" muted
export const BrandName = ({ size = "text-lg" }) => (
  <span className={size}>
    <span className="font-semibold text-white">Upcast</span>
    <span className="font-light text-slate-500">.social</span>
  </span>
)

// Inline brand name for use within prose text (inherits surrounding font weight)
export const InlineBrand = () => (
  <span>Upcast<span className="text-slate-500">.social</span></span>
)

// Replace "Upcast.social" or "Upcast.Social" in a translated string with styled JSX
export function styledBrand(text) {
  if (typeof text !== 'string') return text
  const pattern = /Upcast\.(?:social|Social)/
  if (!pattern.test(text)) return text
  const parts = text.split(/Upcast\.(?:social|Social)/)
  return parts.reduce((acc, part, i) => {
    if (i === 0) return [part]
    return [...acc, <InlineBrand key={i} />, part]
  }, [])
}

// Stats values
const statsData = [
  { value: "Multi-brand", labelKey: "brands" },
  { value: "7", labelKey: "platforms" },
  { value: "10+", labelKey: "hoursSaved" },
  { value: "One", labelKey: "dashboard" }
]

// Testimonial keys
const testimonialKeys = ['item1', 'item2', 'item3']

// Pricing tiers
const pricingTiers = [
  {
    key: 'starter',
    featured: false,
    featureCount: 5,
  },
  {
    key: 'business',
    featured: true,
    featureCount: 6,
  },
]

// Platform Logos (active platforms)
const XLogo = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="white">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
)

const LinkedInLogo = () => (
  <svg className="w-6 h-6" viewBox="0 0 24 24" fill="white">
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
  </svg>
)

const ThreadsLogo = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="white">
    <path d="M12.186 24h-.007c-3.581-.024-6.334-1.205-8.184-3.509C2.35 18.44 1.5 15.586 1.472 12.01v-.017c.03-3.579.879-6.43 2.525-8.482C5.845 1.205 8.6.024 12.18 0h.014c2.746.02 5.043.725 6.826 2.098 1.677 1.29 2.858 3.13 3.509 5.467l-2.04.569c-1.104-3.96-3.898-5.984-8.304-6.015-2.91.022-5.11.936-6.54 2.717C4.307 6.504 3.616 8.914 3.589 12c.027 3.086.718 5.496 2.057 7.164 1.43 1.783 3.631 2.698 6.54 2.717 2.623-.02 4.358-.631 5.8-2.045 1.647-1.613 1.618-3.593 1.09-4.798-.31-.71-.873-1.3-1.634-1.75-.192 1.352-.622 2.446-1.284 3.272-.886 1.102-2.14 1.704-3.73 1.79-1.202.065-2.361-.218-3.259-.801-1.063-.689-1.685-1.74-1.752-2.96-.065-1.182.408-2.256 1.33-3.022.812-.675 1.89-1.058 3.108-1.14.773-.05 1.604-.024 2.458.075.136-.667.19-1.37.128-2.082-.1-1.146-.494-2.041-1.175-2.664-.753-.69-1.856-1.045-3.28-1.054h-.016c-1.2.008-2.195.326-2.957.946-.802.652-1.326 1.596-1.556 2.805l-2.006-.386c.298-1.577 1.003-2.847 2.1-3.78C8.328.947 9.677.502 11.2.48h.029c1.978.013 3.57.553 4.733 1.604.987.89 1.593 2.118 1.802 3.655.088.638.1 1.319.036 2.03 1.175.556 2.108 1.378 2.742 2.452.806 1.364.98 3.042.489 4.72-.658 2.24-2.19 3.792-4.559 4.618-1.435.5-3.053.66-4.654.46zm-.503-5.677c.828-.043 1.471-.313 1.912-.8.603-.666.855-1.632.756-2.904l-.026-.075c-.391-.02-.797-.03-1.214-.03-.776 0-1.474.048-2.074.14-.867.132-1.528.39-1.964.769-.39.341-.577.763-.544 1.222.034.5.284.917.744 1.24.503.354 1.21.499 2.054.499.116 0 .235-.005.356-.012z"/>
  </svg>
)

const BlueSkyLogo = () => (
  <svg className="w-6 h-6" viewBox="0 0 24 24" fill="white">
    <path d="M12 10.8c-1.087-2.114-4.046-6.053-6.798-7.995C2.566.944 1.561 1.266.902 1.565.139 1.908 0 3.08 0 3.768c0 .69.378 5.65.624 6.479.815 2.736 3.713 3.66 6.383 3.364.136-.02.275-.039.415-.056-.138.022-.276.04-.415.056-3.912.58-7.387 2.005-2.83 7.078 5.013 5.19 6.87-1.113 7.823-4.308.953 3.195 2.05 9.271 7.733 4.308 4.267-4.308 1.172-6.498-2.74-7.078a8.741 8.741 0 01-.415-.056c.14.017.279.036.415.056 2.67.297 5.568-.628 6.383-3.364.246-.828.624-5.79.624-6.478 0-.69-.139-1.861-.902-2.206-.659-.298-1.664-.62-4.3 1.24C16.046 4.748 13.087 8.687 12 10.8z"/>
  </svg>
)

const MastodonLogo = () => (
  <svg className="w-6 h-6" viewBox="0 0 24 24" fill="white">
    <path d="M23.268 5.313c-.35-2.578-2.617-4.61-5.304-5.004C17.51.242 15.792 0 11.813 0h-.03c-3.98 0-4.835.242-5.288.309C3.882.692 1.496 2.518.917 5.127.64 6.412.61 7.837.661 9.143c.074 1.874.088 3.745.26 5.611.118 1.24.325 2.47.62 3.68.55 2.237 2.777 4.098 4.96 4.857 2.336.792 4.849.923 7.256.38.265-.061.527-.132.786-.213.585-.184 1.27-.39 1.774-.753a.057.057 0 0 0 .023-.043v-1.809a.052.052 0 0 0-.02-.041.053.053 0 0 0-.046-.01 20.282 20.282 0 0 1-4.709.545c-2.73 0-3.463-1.284-3.674-1.818a5.593 5.593 0 0 1-.319-1.433.053.053 0 0 1 .066-.054c1.517.363 3.072.546 4.632.546.376 0 .75 0 1.125-.01 1.57-.044 3.224-.124 4.768-.422.038-.008.077-.015.11-.024 2.435-.464 4.753-1.92 4.989-5.604.008-.145.03-1.52.03-1.67.002-.512.167-3.63-.024-5.545zm-3.748 9.195h-2.561V8.29c0-1.309-.55-1.976-1.67-1.976-1.23 0-1.846.79-1.846 2.35v3.403h-2.546V8.663c0-1.56-.617-2.35-1.848-2.35-1.112 0-1.668.668-1.67 1.977v6.218H4.822V8.102c0-1.31.337-2.35 1.011-3.12.696-.77 1.608-1.164 2.74-1.164 1.311 0 2.302.5 2.962 1.498l.638 1.06.638-1.06c.66-.999 1.65-1.498 2.96-1.498 1.13 0 2.043.395 2.74 1.164.675.77 1.012 1.81 1.012 3.12z"/>
  </svg>
)

// Coming soon platform logos (greyed out)
const FacebookLogo = () => (
  <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
  </svg>
)

const InstagramLogo = () => (
  <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
  </svg>
)

const TikTokLogo = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/>
  </svg>
)

const YouTubeLogo = () => (
  <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
  </svg>
)

const PinterestLogo = () => (
  <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 0C5.373 0 0 5.372 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738.098.119.112.224.083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.631-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12 0-6.628-5.373-12-12-12z"/>
  </svg>
)

// Active platform card
const ActivePlatformCard = ({ name, logo, bgColor }) => (
  <div className="group bg-slate-900 border border-slate-800 hover:border-violet-500/50 rounded-xl p-6 transition-all duration-300 hover:scale-105">
    <div className={`w-14 h-14 ${bgColor} rounded-xl flex items-center justify-center mx-auto mb-4 shadow-lg`}>
      {logo}
    </div>
    <h4 className="text-lg font-semibold text-white text-center">{name}</h4>
    <div className="flex justify-center mt-2">
      <span className="text-xs font-medium text-green-400 bg-green-500/10 border border-green-500/30 px-2 py-0.5 rounded-full">Connected</span>
    </div>
  </div>
)

// Coming soon platform card
const ComingSoonPlatformCard = ({ name, logo }) => (
  <div className="bg-slate-900/50 border border-slate-800/50 rounded-xl p-6 opacity-50">
    <div className="w-14 h-14 bg-slate-800 rounded-xl flex items-center justify-center mx-auto mb-4 text-slate-500">
      {logo}
    </div>
    <h4 className="text-sm font-semibold text-slate-500 text-center">{name}</h4>
  </div>
)

export default function Landing() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [billingPeriod, setBillingPeriod] = useState('monthly')
  const [showStickyCta, setShowStickyCta] = useState(false)
  const [contactOpen, setContactOpen] = useState(false)
  const [dynamicProducts, setDynamicProducts] = useState(null)
  const { t } = useTranslation('landing')
  const { t: tCommon } = useTranslation('common')

  useEffect(() => {
    const handleScroll = () => {
      setShowStickyCta(window.scrollY > 600)
    }
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  useEffect(() => {
    productsApi.listPublic()
      .then(data => { if (data.products?.length) setDynamicProducts(data.products) })
      .catch(() => {}) // Fall back to hardcoded tiers on failure
  }, [])

  return (
    <div className="min-h-screen bg-slate-950">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-slate-950/90 backdrop-blur-md border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center gap-2">
              <SMMLogo className="w-9 h-9" />
              <BrandName size="text-lg" />
            </Link>

            <div className="hidden md:flex items-center gap-8">
              <a href="/#channels" className="text-slate-400 hover:text-white transition-colors">{tCommon('nav.channels')}</a>
              <Link to="/features" className="text-slate-400 hover:text-white transition-colors">{tCommon('nav.howItWorks')}</Link>
              <a href="#pricing" className="text-slate-400 hover:text-white transition-colors">{tCommon('nav.pricing')}</a>
              <Link to="/partners" className="text-slate-400 hover:text-white transition-colors">{tCommon('nav.partners')}</Link>
              <Link to="/login" className="text-slate-400 hover:text-white transition-colors">{tCommon('nav.signIn')}</Link>
              <Link to="/beta" className="bg-gradient-to-r from-violet-600 to-cyan-600 hover:from-violet-500 hover:to-cyan-500 text-white font-semibold px-5 py-2 rounded-lg transition-all">
                {tCommon('nav.requestAccess')}
              </Link>
            </div>

            {/* Mobile menu button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden text-slate-400 hover:text-white p-2"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {mobileMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>
        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-slate-800 bg-slate-900/95">
            <div className="px-4 py-4 space-y-4">
              <a href="/#channels" onClick={() => setMobileMenuOpen(false)} className="block text-slate-300 hover:text-white py-2">{tCommon('nav.channels')}</a>
              <Link to="/features" onClick={() => setMobileMenuOpen(false)} className="block text-slate-300 hover:text-white py-2">{tCommon('nav.howItWorks')}</Link>
              <a href="#pricing" onClick={() => setMobileMenuOpen(false)} className="block text-slate-300 hover:text-white py-2">{tCommon('nav.pricing')}</a>
              <Link to="/partners" onClick={() => setMobileMenuOpen(false)} className="block text-slate-300 hover:text-white py-2">{tCommon('nav.partners')}</Link>
              <div className="pt-4 border-t border-slate-800 space-y-4">
                <Link to="/login" onClick={() => setMobileMenuOpen(false)} className="block text-slate-300 hover:text-white py-2">{tCommon('nav.signIn')}</Link>
                <Link to="/beta" onClick={() => setMobileMenuOpen(false)} className="block bg-gradient-to-r from-violet-600 to-cyan-600 text-white font-semibold px-5 py-3 rounded-lg text-center">
                  {tCommon('nav.requestAccess')}
                </Link>
              </div>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <header className="relative overflow-hidden">
        {/* Background effects */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 left-1/4 w-96 h-96 bg-violet-600/20 rounded-full filter blur-[128px]"></div>
          <div className="absolute bottom-20 right-1/4 w-96 h-96 bg-cyan-600/20 rounded-full filter blur-[128px]"></div>
        </div>

        {/* Rotating platform orbit */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none hidden md:block">
          <div className="hero-orbit w-[500px] h-[500px] lg:w-[600px] lg:h-[600px] opacity-[0.07]">
            <div className="absolute w-full h-full rounded-full border border-slate-500/30"></div>
            {[
              { angle: 0, icon: <LinkedInLogo /> },
              { angle: 51.4, icon: <XLogo /> },
              { angle: 102.8, icon: <ThreadsLogo /> },
              { angle: 154.3, icon: <BlueSkyLogo /> },
              { angle: 205.7, icon: <MastodonLogo /> },
              { angle: 257.1, icon: <FacebookLogo /> },
              { angle: 308.6, icon: <InstagramLogo /> },
            ].map(({ angle, icon }, i) => {
              const rad = (angle * Math.PI) / 180
              const r = 50
              const x = Math.cos(rad) * r
              const y = Math.sin(rad) * r
              return (
                <div
                  key={i}
                  className="hero-orbit-icon"
                  style={{ marginLeft: `${x}%`, marginTop: `${y}%` }}
                >
                  <div className="w-14 h-14 lg:w-16 lg:h-16 flex items-center justify-center text-white [&>svg]:w-10 [&>svg]:h-10 lg:[&>svg]:w-12 lg:[&>svg]:h-12">
                    {icon}
                  </div>
                </div>
              )
            })}
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-28 md:py-36 relative">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center bg-violet-500/10 border border-violet-500/30 rounded-full px-4 py-2 mb-8">
              <span className="w-2 h-2 bg-violet-500 rounded-full mr-2 animate-pulse"></span>
              <span className="text-violet-400 text-sm font-medium">{t('hero.badge')}</span>
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-black leading-[1.1] mb-8">
              <span className="text-white">{t('hero.title1')}</span>{" "}
              <span className="bg-gradient-to-r from-violet-500 to-cyan-500 bg-clip-text text-transparent">{t('hero.title2')}</span>
            </h1>

            <p className="text-xl md:text-2xl text-slate-400 mb-10 leading-relaxed max-w-3xl mx-auto">
              {styledBrand(t('hero.description'))}
              <strong className="text-white">{t('hero.descriptionBold')}</strong>
            </p>

            <div className="overflow-hidden mb-10">
              <div className="marquee-track">
                {[...Array(2)].map((_, setIndex) => (
                  <div key={setIndex} className="flex gap-3 shrink-0">
                    <span className="flex items-center gap-2 bg-slate-900/80 border border-slate-800 rounded-full px-4 py-2 text-sm font-medium text-slate-300 whitespace-nowrap">
                      <span className="w-2 h-2 rounded-full bg-[#0A66C2]"></span>LinkedIn
                    </span>
                    <span className="flex items-center gap-2 bg-slate-900/80 border border-slate-800 rounded-full px-4 py-2 text-sm font-medium text-slate-300 whitespace-nowrap">
                      <span className="w-2 h-2 rounded-full bg-white"></span>X
                    </span>
                    <span className="flex items-center gap-2 bg-slate-900/80 border border-slate-800 rounded-full px-4 py-2 text-sm font-medium text-slate-300 whitespace-nowrap">
                      <span className="w-2 h-2 rounded-full bg-white"></span>Threads
                    </span>
                    <span className="flex items-center gap-2 bg-slate-900/80 border border-slate-800 rounded-full px-4 py-2 text-sm font-medium text-slate-300 whitespace-nowrap">
                      <span className="w-2 h-2 rounded-full bg-[#0085FF]"></span>Bluesky
                    </span>
                    <span className="flex items-center gap-2 bg-slate-900/80 border border-slate-800 rounded-full px-4 py-2 text-sm font-medium text-slate-300 whitespace-nowrap">
                      <span className="w-2 h-2 rounded-full bg-[#6364FF]"></span>Mastodon
                    </span>
                    <span className="flex items-center gap-2 bg-slate-900/80 border border-slate-800 rounded-full px-4 py-2 text-sm font-medium text-slate-300 whitespace-nowrap">
                      <span className="w-2 h-2 rounded-full bg-[#1877F2]"></span>Facebook
                    </span>
                    <span className="flex items-center gap-2 bg-slate-900/80 border border-slate-800 rounded-full px-4 py-2 text-sm font-medium text-slate-300 whitespace-nowrap">
                      <span className="w-2 h-2 rounded-full bg-[#E4405F]"></span>Instagram
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/beta" className="bg-gradient-to-r from-violet-600 to-cyan-600 hover:from-violet-500 hover:to-cyan-500 text-white font-bold px-8 py-4 rounded-xl text-lg transition-all transform hover:scale-105 shadow-lg shadow-violet-600/25">
                {t('hero.requestAccess')}
              </Link>
              <Link to="/features" className="border-2 border-slate-700 hover:border-violet-500 text-slate-300 hover:text-white font-semibold px-8 py-4 rounded-xl text-lg transition-all">
                {t('hero.seeFeatures')}
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Stats Section */}
      <section className="py-20 bg-slate-900/50 border-y border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {statsData.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl md:text-5xl font-black bg-gradient-to-r from-violet-500 to-cyan-500 bg-clip-text text-transparent mb-2">
                  {stat.value}
                </div>
                <div className="text-slate-400">{t(`stats.${stat.labelKey}`)}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Problem/Solution Section */}
      <section className="py-24 md:py-36">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-start">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold mb-8">
                <span className="text-white">{t('problem.title')}</span>{" "}
                <span className="text-red-400">{t('problem.titleHighlight')}</span>
              </h2>
              <div className="space-y-5 text-slate-400">
                <p className="flex items-start gap-3">
                  <span className="text-red-400 mt-1">&#10005;</span>
                  {t('problem.item1')}
                </p>
                <p className="flex items-start gap-3">
                  <span className="text-red-400 mt-1">&#10005;</span>
                  {t('problem.item2')}
                </p>
                <p className="flex items-start gap-3">
                  <span className="text-red-400 mt-1">&#10005;</span>
                  {t('problem.item3')}
                </p>
                <p className="flex items-start gap-3">
                  <span className="text-red-400 mt-1">&#10005;</span>
                  {t('problem.item4')}
                </p>
              </div>
            </div>

            <div>
              <h2 className="text-3xl md:text-4xl font-bold mb-8">
                <span className="text-white">{t('solution.title')}</span>{" "}
                <span className="bg-gradient-to-r from-violet-500 to-cyan-500 bg-clip-text text-transparent">{t('solution.titleHighlight')}</span>
              </h2>
              <div className="space-y-5 text-slate-400">
                <p className="flex items-start gap-3">
                  <span className="text-green-400 mt-1">&#10003;</span>
                  <span><strong className="text-white">{t('solution.item1Bold')}</strong> {t('solution.item1')}</span>
                </p>
                <p className="flex items-start gap-3">
                  <span className="text-green-400 mt-1">&#10003;</span>
                  <span><strong className="text-white">{t('solution.item2Bold')}</strong> {t('solution.item2')}</span>
                </p>
                <p className="flex items-start gap-3">
                  <span className="text-green-400 mt-1">&#10003;</span>
                  <span><strong className="text-white">{t('solution.item3Bold')}</strong> {t('solution.item3')}</span>
                </p>
                <p className="flex items-start gap-3">
                  <span className="text-green-400 mt-1">&#10003;</span>
                  <span><strong className="text-white">{t('solution.item4Bold')}</strong> {t('solution.item4')}</span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-24 md:py-36 bg-slate-900/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              <span className="text-white">{t('testimonials.title')}</span>{" "}
              <span className="bg-gradient-to-r from-violet-500 to-cyan-500 bg-clip-text text-transparent">{t('testimonials.titleHighlight')}</span>
            </h2>
          </div>

          <div className="max-w-2xl mx-auto mb-8">
            {['item1'].map((key) => (
              <div
                key={key}
                className="bg-slate-900 border border-slate-800 hover:border-violet-500/50 rounded-2xl p-8 transition-all duration-300"
              >
                <p className="text-slate-300 leading-relaxed mb-6 italic">
                  &ldquo;{styledBrand(t(`testimonials.${key}.quote`))}&rdquo;
                </p>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white font-semibold">{t(`testimonials.${key}.author`)}</p>
                    <a href={t(`testimonials.${key}.url`)} target="_blank" rel="noopener noreferrer" className="text-slate-500 hover:text-violet-400 text-sm transition-colors">{t(`testimonials.${key}.role`)}</a>
                  </div>
                  <span className="text-xs font-medium text-violet-400 bg-violet-500/10 border border-violet-500/30 px-3 py-1 rounded-full">
                    {t(`testimonials.${key}.metric`)}
                  </span>
                </div>
              </div>
            ))}
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {['item2', 'item3'].map((key) => (
              <div
                key={key}
                className="bg-slate-900 border border-slate-800 hover:border-violet-500/50 rounded-2xl p-8 transition-all duration-300"
              >
                <p className="text-slate-300 leading-relaxed mb-6 italic">
                  &ldquo;{styledBrand(t(`testimonials.${key}.quote`))}&rdquo;
                </p>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white font-semibold">{t(`testimonials.${key}.author`)}</p>
                    <a href={t(`testimonials.${key}.url`)} target="_blank" rel="noopener noreferrer" className="text-slate-500 hover:text-violet-400 text-sm transition-colors">{t(`testimonials.${key}.role`)}</a>
                  </div>
                  <span className="text-xs font-medium text-violet-400 bg-violet-500/10 border border-violet-500/30 px-3 py-1 rounded-full">
                    {t(`testimonials.${key}.metric`)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-24 md:py-36">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">
              {styledBrand(t('howItWorks.title'))}
            </h2>
            <p className="text-xl text-slate-400">
              {t('howItWorks.description')}
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-8 mb-16">
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-violet-600 to-violet-700 rounded-2xl flex items-center justify-center mx-auto mb-6 text-3xl font-black text-white">
                1
              </div>
              <h3 className="text-xl font-bold text-white mb-3">{t('howItWorks.step1.title')}</h3>
              <p className="text-slate-400">
                {t('howItWorks.step1.description')}
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-violet-600 to-cyan-600 rounded-2xl flex items-center justify-center mx-auto mb-6 text-3xl font-black text-white">
                2
              </div>
              <h3 className="text-xl font-bold text-white mb-3">{t('howItWorks.step2.title')}</h3>
              <p className="text-slate-400">
                {t('howItWorks.step2.description')}
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-cyan-600 to-cyan-700 rounded-2xl flex items-center justify-center mx-auto mb-6 text-3xl font-black text-white">
                3
              </div>
              <h3 className="text-xl font-bold text-white mb-3">{t('howItWorks.step3.title')}</h3>
              <p className="text-slate-400">
                {t('howItWorks.step3.description')}
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-cyan-700 to-emerald-600 rounded-2xl flex items-center justify-center mx-auto mb-6 text-3xl font-black text-white">
                4
              </div>
              <h3 className="text-xl font-bold text-white mb-3">{t('howItWorks.step4.title')}</h3>
              <p className="text-slate-400">
                {t('howItWorks.step4.description')}
              </p>
            </div>
          </div>

          {/* How It Works CTA */}
          <div className="text-center">
            <Link to="/features" className="inline-block border-2 border-slate-700 hover:border-violet-500 text-slate-300 hover:text-white font-semibold px-8 py-4 rounded-xl text-lg transition-all">
              {t('howItWorks.learnMore')}
            </Link>
          </div>
        </div>
      </section>

      {/* Channels Section */}
      <section id="channels" className="py-24 md:py-36 bg-slate-900/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              <span className="text-white">{t('channels.sectionTitle')}</span>{" "}
              <span className="bg-gradient-to-r from-violet-500 to-cyan-500 bg-clip-text text-transparent">{t('channels.sectionTitleHighlight')}</span>
            </h2>
            <p className="text-xl text-slate-400">
              {t('channels.sectionDescription')}
            </p>
          </div>

          {/* Active Platforms */}
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-6 max-w-5xl mx-auto mb-16">
            <ActivePlatformCard name="LinkedIn" logo={<LinkedInLogo />} bgColor="bg-[#0A66C2]" />
            <ActivePlatformCard name="X" logo={<XLogo />} bgColor="bg-black" />
            <ActivePlatformCard name="Threads" logo={<ThreadsLogo />} bgColor="bg-black" />
            <ActivePlatformCard name="Bluesky" logo={<BlueSkyLogo />} bgColor="bg-[#0085FF]" />
            <ActivePlatformCard name="Mastodon" logo={<MastodonLogo />} bgColor="bg-[#6364FF]" />
            <ActivePlatformCard name="Facebook" logo={<FacebookLogo />} bgColor="bg-[#1877F2]" />
            <ActivePlatformCard name="Instagram" logo={<InstagramLogo />} bgColor="bg-gradient-to-br from-[#F58529] via-[#DD2A7B] to-[#8134AF]" />
          </div>

          {/* Coming Soon */}
          <div className="text-center mb-8">
            <span className="text-slate-500 text-sm font-medium uppercase tracking-wider">{t('channels.comingSoon')}</span>
          </div>
          <div className="grid grid-cols-3 gap-4 max-w-xl mx-auto">
            <ComingSoonPlatformCard name="TikTok" logo={<TikTokLogo />} />
            <ComingSoonPlatformCard name="YouTube" logo={<YouTubeLogo />} />
            <ComingSoonPlatformCard name="Pinterest" logo={<PinterestLogo />} />
          </div>
        </div>
      </section>

      {/* Pricing Preview */}
      <section id="pricing" className="py-24 md:py-36">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <div className="inline-flex items-center bg-violet-500/10 border border-violet-500/30 rounded-full px-4 py-2 mb-6">
              <span className="w-2 h-2 bg-violet-500 rounded-full mr-2 animate-pulse"></span>
              <span className="text-violet-400 text-sm font-medium">{t('hero.badge')}</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              {t('pricing.title')}
            </h2>
            <p className="text-xl text-slate-400 mb-8">
              {t('pricing.description')}
            </p>

            {/* Billing toggle */}
            <div className="inline-flex items-center gap-3 bg-slate-900 border border-slate-800 rounded-full px-2 py-1">
              <button
                onClick={() => setBillingPeriod('monthly')}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  billingPeriod === 'monthly'
                    ? 'bg-violet-600 text-white'
                    : 'text-slate-400 hover:text-white'
                }`}
              >
                {t('pricing.monthly')}
              </button>
              <button
                onClick={() => setBillingPeriod('yearly')}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-2 ${
                  billingPeriod === 'yearly'
                    ? 'bg-violet-600 text-white'
                    : 'text-slate-400 hover:text-white'
                }`}
              >
                {t('pricing.yearly')}
                {!dynamicProducts && (
                  <span className="text-xs text-green-400 font-semibold">{t('pricing.yearlyDiscount')}</span>
                )}
              </button>
            </div>
          </div>

          <div className={`grid gap-8 items-start ${(dynamicProducts || pricingTiers).length <= 2 ? 'md:grid-cols-2' : 'md:grid-cols-3'}`}>
            {dynamicProducts ? (
              dynamicProducts.map((product, idx) => {
                const isFeatured = dynamicProducts.length <= 2 ? idx === dynamicProducts.length - 1 : idx === 1
                const perUserPrice = billingPeriod === 'monthly'
                  ? product.monthly_price_gbp
                  : product.annual_price_gbp / 12
                const minUsers = product.min_users || 1
                const displayPrice = Math.round(perUserPrice * minUsers)
                const price = `£${displayPrice}`
                const annualDiscount = product.monthly_price_gbp > 0 && product.annual_price_gbp > 0
                  ? Math.round((1 - product.annual_price_gbp / (product.monthly_price_gbp * 12)) * 100)
                  : 0
                return (
                  <div
                    key={product.product_id}
                    className={`rounded-2xl transition-all duration-300 ${
                      isFeatured
                        ? 'bg-gradient-to-b from-violet-900/30 to-slate-900 border-2 border-violet-500/50 p-8 md:p-10 md:scale-105 relative'
                        : 'bg-slate-900 border border-slate-800 p-8'
                    }`}
                  >
                    {isFeatured && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                        <span className="bg-gradient-to-r from-violet-600 to-cyan-600 text-white text-xs font-semibold px-4 py-1 rounded-full">
                          Most Popular
                        </span>
                      </div>
                    )}
                    <h3 className="text-2xl font-bold text-white mb-2">{product.name}</h3>
                    {billingPeriod === 'yearly' && annualDiscount > 0 && (
                      <span className="text-xs text-green-400 font-semibold bg-green-500/10 px-2 py-1 rounded-full inline-block mb-3">
                        Save {annualDiscount}% with annual billing
                      </span>
                    )}
                    {product.tagline && <p className="text-slate-400 text-sm mb-4">{product.tagline}</p>}
                    <div className="flex items-baseline gap-2 mb-1">
                      <span className="text-4xl font-black text-white">{price}</span>
                      <span className="text-slate-400">/ month</span>
                    </div>
                    {minUsers > 1 ? (
                      <div className="text-sm text-slate-500 mb-4">
                        <p>£{Math.round(perUserPrice)} per user / month</p>
                        <p>{minUsers} users minimum{product.max_users > minUsers ? `, up to ${product.max_users}` : ''}</p>
                      </div>
                    ) : (
                      <div className="text-sm text-slate-500 mb-4">
                        {product.max_users === 1 ? (
                          <p>For 1 user</p>
                        ) : product.max_users > 1 ? (
                          <>
                            <p>£{Math.round(perUserPrice)} per user / month</p>
                            <p>Up to {product.max_users} users</p>
                          </>
                        ) : (
                          <>
                            <p>£{Math.round(perUserPrice)} per user / month</p>
                            <p>Unlimited users</p>
                          </>
                        )}
                      </div>
                    )}
                    {billingPeriod === 'yearly' && (
                      <p className="text-sm text-slate-500 -mt-4 mb-6">{t('pricing.billedYearly')}</p>
                    )}
                    <ul className="space-y-3 mb-8">
                      <li className="flex items-center gap-3 text-slate-300">
                        <span className="text-green-400">&#10003;</span>
                        {product.max_brands} {product.max_brands === 1 ? 'brand' : 'brands'}
                      </li>
                      <li className="flex items-center gap-3 text-slate-300">
                        <span className="text-green-400">&#10003;</span>
                        {product.max_platform_connections} platform {product.max_platform_connections === 1 ? 'connection' : 'connections'}
                      </li>
                      <li className="flex items-center gap-3 text-slate-300">
                        <span className="text-green-400">&#10003;</span>
                        {product.max_drafts_per_month.toLocaleString()} assisted drafts/month
                      </li>
                      <li className="flex items-center gap-3 text-slate-300">
                        <span className="text-green-400">&#10003;</span>
                        {product.daily_auto_posts} daily auto-generated posts
                      </li>
                      {product.max_blog_posts_per_month > 0 && (
                        <li className="flex items-center gap-3 text-slate-300">
                          <span className="text-green-400">&#10003;</span>
                          {product.max_blog_posts_per_month} blog posts/month
                        </li>
                      )}
                      {(product.marketing_features || []).map((feat, i) => (
                        <li key={i} className="flex items-center gap-3 text-slate-300">
                          <span className="text-green-400">&#10003;</span>
                          {feat}
                        </li>
                      ))}
                    </ul>
                    <Link
                      to="/beta"
                      className={`block text-center font-semibold px-6 py-3 rounded-xl transition-all ${
                        isFeatured
                          ? 'bg-gradient-to-r from-violet-600 to-cyan-600 hover:from-violet-500 hover:to-cyan-500 text-white'
                          : 'border border-slate-700 hover:border-violet-500 text-slate-300 hover:text-white'
                      }`}
                    >
                      {t('pricing.cta')}
                    </Link>
                  </div>
                )
              })
            ) : (
              pricingTiers.map((tier) => (
                <div
                  key={tier.key}
                  className={`rounded-2xl transition-all duration-300 ${
                    tier.featured
                      ? 'bg-gradient-to-b from-violet-900/30 to-slate-900 border-2 border-violet-500/50 p-8 md:p-10 md:scale-105 relative'
                      : 'bg-slate-900 border border-slate-800 p-8'
                  }`}
                >
                  {tier.featured && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <span className="bg-gradient-to-r from-violet-600 to-cyan-600 text-white text-xs font-semibold px-4 py-1 rounded-full">
                        {t(`pricing.${tier.key}.badge`)}
                      </span>
                    </div>
                  )}
                  <h3 className="text-2xl font-bold text-white mb-4">{t(`pricing.${tier.key}.name`)}</h3>
                  <div className="flex items-baseline gap-1 mb-6">
                    <span className="text-4xl font-black text-white">
                      {billingPeriod === 'monthly'
                        ? t(`pricing.${tier.key}.priceMonthly`)
                        : t(`pricing.${tier.key}.priceYearly`)
                      }
                    </span>
                    <span className="text-slate-400">{t('pricing.perMonth')}</span>
                  </div>
                  {billingPeriod === 'yearly' && (
                    <p className="text-sm text-slate-500 -mt-4 mb-6">{t('pricing.billedYearly')}</p>
                  )}
                  <ul className="space-y-3 mb-8">
                    {Array.from({ length: tier.featureCount }, (_, i) => (
                      <li key={i} className="flex items-center gap-3 text-slate-300">
                        <span className="text-green-400">&#10003;</span>
                        {t(`pricing.${tier.key}.feature${i + 1}`)}
                      </li>
                    ))}
                  </ul>
                  <Link
                    to="/beta"
                    className={`block text-center font-semibold px-6 py-3 rounded-xl transition-all ${
                      tier.featured
                        ? 'bg-gradient-to-r from-violet-600 to-cyan-600 hover:from-violet-500 hover:to-cyan-500 text-white'
                        : 'border border-slate-700 hover:border-violet-500 text-slate-300 hover:text-white'
                    }`}
                  >
                    {t('pricing.cta')}
                  </Link>
                </div>
              ))
            )}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-36 bg-gradient-to-br from-violet-900/50 to-cyan-900/50 border-y border-slate-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-black text-white mb-6">
            {t('cta.title')}
          </h2>
          <p className="text-xl text-slate-300 mb-10">
            {t('cta.description')}
          </p>
          <Link to="/beta" className="inline-block bg-white hover:bg-slate-100 text-slate-900 font-bold px-10 py-5 rounded-xl text-lg transition-all transform hover:scale-105">
            {t('cta.button')}
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-16 border-t border-slate-800 pb-28 md:pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-12 mb-12">
            {/* Brand */}
            <div className="col-span-2 md:col-span-1">
              <div className="flex items-center gap-2 mb-4">
                <SMMLogo className="w-8 h-8" />
                <BrandName size="text-lg" />
              </div>
              <p className="text-slate-500 text-sm">{t('footer.tagline')}</p>
            </div>

            {/* Product links */}
            <div>
              <h4 className="text-sm font-semibold text-white uppercase tracking-wider mb-4">{t('footer.product')}</h4>
              <ul className="space-y-3 text-sm">
                <li><a href="/#channels" className="text-slate-400 hover:text-white transition-colors">{tCommon('nav.channels')}</a></li>
                <li><Link to="/features" className="text-slate-400 hover:text-white transition-colors">{tCommon('nav.howItWorks')}</Link></li>
                <li><a href="#pricing" className="text-slate-400 hover:text-white transition-colors">{t('footer.pricing')}</a></li>
              </ul>
            </div>

            {/* Company links */}
            <div>
              <h4 className="text-sm font-semibold text-white uppercase tracking-wider mb-4">{t('footer.company')}</h4>
              <ul className="space-y-3 text-sm">
                <li><Link to="/privacy" className="text-slate-400 hover:text-white transition-colors">{tCommon('footer.privacy')}</Link></li>
                <li><Link to="/terms" className="text-slate-400 hover:text-white transition-colors">{tCommon('footer.terms')}</Link></li>
                <li><button onClick={() => setContactOpen(true)} className="text-slate-400 hover:text-white transition-colors">{tCommon('footer.contact')}</button></li>
              </ul>
            </div>

            {/* Footer CTA */}
            <div>
              <Link to="/beta" className="inline-block bg-gradient-to-r from-violet-600 to-cyan-600 hover:from-violet-500 hover:to-cyan-500 text-white font-semibold px-6 py-3 rounded-xl transition-all">
                {t('footer.cta')}
              </Link>
            </div>
          </div>

          <div className="border-t border-slate-800 pt-8">
            <p className="text-slate-500 text-sm text-center">
              {tCommon('footer.copyright')}
            </p>
          </div>
        </div>
      </footer>

      {/* Sticky mobile CTA */}
      {showStickyCta && (
        <div className="fixed bottom-0 left-0 right-0 z-40 md:hidden bg-slate-950/95 backdrop-blur-md border-t border-slate-800 px-4 py-3">
          <Link to="/beta" className="block bg-gradient-to-r from-violet-600 to-cyan-600 text-white font-semibold py-3 rounded-xl text-center">
            {t('hero.requestAccess')}
          </Link>
        </div>
      )}

      <ContactModal isOpen={contactOpen} onClose={() => setContactOpen(false)} />
    </div>
  )
}
